import { useRef } from 'react';
import { UploadCloud, CheckCircle2, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocUploadCard({
  label,
  required,
  file,
  existingPath,
  onChange,
  onView,
  disabled = false,
}: {
  label: string;
  required: boolean;
  file: File | null;
  existingPath?: string;
  onChange: (file: File | null) => void;
  onView?: (path: string) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const filled = !!file || !!existingPath;

  const activate = () => {
    if (file) return;
    if (existingPath) {
      onView?.(existingPath);
      return;
    }
    if (!disabled) inputRef.current?.click();
  };

  return (
    <div
      className={`group relative rounded-xl border p-3.5 transition-colors duration-150 ${
        filled ? 'border-success-border/60 bg-success-bg/40' : 'border-dashed border-border hover:border-primary-300 hover:bg-primary-50/40'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        disabled={disabled}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={activate}
          className={`w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center cursor-pointer ${
            filled ? 'bg-success text-white' : 'bg-surface-muted text-ink-faint group-hover:text-primary-700 group-hover:bg-primary-100'
          }`}
        >
          {filled ? <CheckCircle2 className="w-4.5 h-4.5" /> : <UploadCloud className="w-4.5 h-4.5" />}
        </button>
        <button type="button" onClick={activate} className="min-w-0 flex-1 text-left cursor-pointer">
          <div className="flex items-center gap-1.5">
            <p className="text-[12.5px] font-semibold text-ink truncate">{label}</p>
            <Badge tone={required ? 'primary' : 'neutral'}>{required ? 'Required' : 'Optional'}</Badge>
          </div>
          {file ? (
            <p className="text-[11px] text-ink-faint truncate">
              {file.name} · {formatFileSize(file.size)}
            </p>
          ) : existingPath ? (
            <p className="text-[11px] text-ink-faint truncate">On file — click to view</p>
          ) : (
            <p className="text-[11px] text-ink-faint">Click to upload — JPG, PNG or PDF</p>
          )}
        </button>
        {file && (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              if (inputRef.current) inputRef.current.value = '';
            }}
            className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-md text-ink-faint hover:bg-white hover:text-error cursor-pointer"
            aria-label={`Remove ${label}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {!file && existingPath && !disabled && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="text-[10.5px] font-semibold text-primary-800 hover:underline flex-shrink-0 cursor-pointer"
          >
            Replace
          </button>
        )}
      </div>
    </div>
  );
}
